import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAdminReport } from '../../hooks/useQuestAdmin';
import {
  CATEGORY, currentQuarter, downloadCsv, periodLabel,
} from './questShared';
import {
  BTN_SECONDARY, CsvButton, Loading, PeriodSelect, Section, Stat,
} from './adminUi';

// Reporting (brief section 10): who took part, how each Quest did, month by
// month participation — the start of answering "is Nano Quest actually
// increasing engagement?" — and recognition: nominations, the most recognised
// people and values, customer compliments and the other submissions, training,
// and participation by cluster (no department is kept anywhere).
//
// The headline numbers sit on top; each report below is its own expandable
// section with its own download, Quest performance open to start.

const MONTH = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

const SUBMISSION_TITLE = {
  compliment: 'Customer compliments',
  improvement: 'Improvement ideas',
  colleague: 'Colleague thanks',
  task: 'Challenge entries',
};

const Empty = ({ children }) => <p className="px-4 py-6 text-center text-sm text-gray-400">{children}</p>;
const ROW = 'flex justify-between px-4 py-2 text-sm';
const TH = 'px-4 py-2 text-right';
const TD = 'px-4 py-2 text-right tabular-nums';

// Quarter on quarter: this period's quarter and the three before it (or a
// year's four), with the change from the quarter before.
function Quarters({ rows }) {
  const cols = [
    ['people_earning', 'People earning'],
    ['points_awarded', 'Points'],
    ['quests_passed', 'Quests passed'],
    ['nominations', 'Nominations'],
    ['submissions', 'Submissions'],
  ];
  const change = (now, before) => {
    if (before === undefined) return null;
    if (!before) return now ? 'new' : null;
    const pct = Math.round(((now - before) / before) * 100);
    return `${pct > 0 ? '+' : ''}${pct}%`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2">Quarter</th>
            {cols.map(([k, label]) => <th key={k} className={TH}>{label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={row.quarter}>
              <td className="px-4 py-2 font-medium text-gray-900">{periodLabel(row.quarter)}</td>
              {cols.map(([k]) => {
                const c = change(row[k], i ? rows[i - 1][k] : undefined);
                return (
                  <td key={k} className={TD}>
                    {row[k]}
                    {c ? <span className={`ml-1 text-xs ${c.startsWith('-') ? 'text-rose-600' : 'text-emerald-700'}`}>{c}</span> : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Recognition: the counts, then the most recognised people and values side by side.
function Recognition({ rec }) {
  const list = (title, items, empty, render) => (
    <div className="rounded-md border border-gray-100">
      <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      {!items.length ? <Empty>{empty}</Empty> : <ul className="divide-y divide-gray-100">{items.map(render)}</ul>}
    </div>
  );
  return (
    <div className="space-y-3 p-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Stat label="Nominations" value={rec.nominations.total}
          sub={`${rec.nominations.approved} approved · ${rec.nominations.waiting} waiting`} />
        {rec.submissions.map((x) => (
          <Stat key={x.kind} label={SUBMISSION_TITLE[x.kind] || x.label} value={x.total}
            sub={`${x.approved} approved · ${x.waiting} waiting`} />
        ))}
        <Stat label="Training completed" value={rec.training_completed} sub="assigned courses passed" />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {list('Most recognised people', rec.most_recognised, 'No recognition yet.', (x, i) => (
          <li key={x.entra_user_id} className={ROW}>
            <span className="text-gray-900">{i + 1}. {x.name}</span>
            <span className="tabular-nums text-gray-600">{x.recognitions}</span>
          </li>
        ))}
        {list('Most recognised values', rec.values, 'No approved nominations yet.', (v) => (
          <li key={v.value_key} className={ROW}>
            <span className="text-gray-900">{v.title}</span>
            <span className="tabular-nums text-gray-600">{v.nominations}</span>
          </li>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [period, setPeriod] = useState(currentQuarter());
  const q = useAdminReport(period);
  const r = q.data;

  const exportEmployees = () => downloadCsv(`nano-quest-engagement-${period}.csv`, [
    ['Rank', (e) => e.position],
    ['Name', (e) => e.name],
    ['Job title', (e) => e.job_title],
    ['Region', (e) => e.region],
    ['Cluster', (e) => e.cluster],
    ['Points', (e) => e.points],
    ['Quests passed', (e) => e.quests_completed],
    ['Average quiz score %', (e) => e.average_score],
    ['Quiz points', (e) => e.points_by_category.quiz || 0],
    ['Values points', (e) => e.points_by_category.values || 0],
    ['Colleague points', (e) => e.points_by_category.colleague || 0],
    ['Compliment points', (e) => e.points_by_category.compliment || 0],
    ['Improvement points', (e) => e.points_by_category.improvement || 0],
    ['Training points', (e) => e.points_by_category.training || 0],
    ['Competition points', (e) => e.points_by_category.competition || 0],
    ['Manual adjustments', (e) => e.points_by_category.manual || 0],
  ], r?.employees || []);

  const exportQuests = () => downloadCsv(`nano-quest-performance-${period}.csv`, [
    ['Quest', (x) => x.title],
    ['Category', (x) => CATEGORY[x.category] || x.category],
    ['Type', (x) => x.quest_type],
    ['Participants', (x) => x.participants],
    ['Passed', (x) => x.passed],
    ['Completion rate %', (x) => x.completion_rate],
    ['Average score %', (x) => x.average_score],
    ['Average attempts', (x) => x.average_attempts],
    ['Points awarded', (x) => x.points_awarded],
    ['Submissions', (x) => x.submissions],
  ], r?.quests || []);

  const exportQuarters = () => downloadCsv(`nano-quest-quarters-${period}.csv`, [
    ['Quarter', (x) => periodLabel(x.quarter)],
    ['People earning points', (x) => x.people_earning],
    ['Points awarded', (x) => x.points_awarded],
    ['Quests passed', (x) => x.quests_passed],
    ['Nominations', (x) => x.nominations],
    ['Submissions', (x) => x.submissions],
  ], r?.quarters || []);

  const exportRecognition = () => downloadCsv(`nano-quest-recognition-${period}.csv`, [
    ['Name', (x) => x.name],
    ['Recognitions received', (x) => x.recognitions],
  ], r?.recognition?.most_recognised || []);

  const maxPeople = Math.max(1, ...(r?.months || []).map((m) => m.people_earning));
  const clusters = r?.participation_by_cluster || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodSelect value={period} onChange={setPeriod} />
        <button type="button" onClick={exportEmployees} disabled={!r?.employees.length} className={`${BTN_SECONDARY} ml-auto`}
          title="Everyone's points, Quests passed and quiz scores for the period">
          <ArrowDownTrayIcon className="h-4 w-4" /> Engagement by person (CSV)
        </button>
      </div>

      <Loading q={q} what="the report" />
      {r ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Taking part" value={r.employees_taking_part}
              sub={`of ${r.employees_total} employees (${r.employees_total ? Math.round((r.employees_taking_part / r.employees_total) * 100) : 0}%)`} />
            <Stat label="Quests" value={r.quests.length} sub={periodLabel(r.period)} />
            <Stat label="Points awarded" value={r.employees.reduce((t, e) => t + e.points, 0)} />
            <Stat label="Quests passed" value={r.employees.reduce((t, e) => t + e.quests_completed, 0)} />
          </div>

          <Section defaultOpen title="Quest performance" description="How each Quest did: who took part, passed, scores and points."
            badge={r.quests.length} action={<CsvButton onClick={exportQuests} disabled={!r.quests.length} />}>
            {!r.quests.length ? <Empty>No Quests in this period.</Empty> : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2">Quest</th>
                      <th className={TH}>Took part</th>
                      <th className={TH}>Passed</th>
                      <th className={TH}>Avg score</th>
                      <th className={TH}>Avg attempts</th>
                      <th className={TH}>Points</th>
                      <th className={TH}>Submissions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {r.quests.map((x) => (
                      <tr key={x.id}>
                        <td className="px-4 py-2 font-medium text-gray-900">{x.title}</td>
                        <td className={TD}>{x.participants}</td>
                        <td className={TD}>{x.passed}{x.completion_rate != null ? ` (${x.completion_rate}%)` : ''}</td>
                        <td className={TD}>{x.average_score != null ? `${x.average_score}%` : '—'}</td>
                        <td className={TD}>{x.average_attempts ?? '—'}</td>
                        <td className={TD}>{x.points_awarded}</td>
                        <td className={TD}>{x.submissions || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="Month by month" description="People earning points each month — is engagement growing?">
            {!r.months.length ? <Empty>Nothing yet in this period.</Empty> : (
              <ul className="space-y-2 p-4">
                {r.months.map((m) => (
                  <li key={m.month} className="flex items-center gap-3 text-sm">
                    <span className="w-20 shrink-0 text-gray-600">{MONTH(m.month)}</span>
                    <span className="h-3 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <span className="block h-full rounded-full bg-brand" style={{ width: `${(m.people_earning / maxPeople) * 100}%` }} />
                    </span>
                    <span className="w-40 shrink-0 text-right tabular-nums text-gray-700">{m.people_earning} people · {m.points_awarded} pts</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {r.quarters ? (
            <Section title="Quarter on quarter" description="This quarter against the ones before it."
              action={<CsvButton onClick={exportQuarters} disabled={!r.quarters.length} />}>
              <Quarters rows={r.quarters} />
            </Section>
          ) : null}

          {r.recognition ? (
            <Section title="Recognition"
              description="Nominations, compliments, ideas and thanks — and who and which values were recognised most."
              action={<CsvButton onClick={exportRecognition} disabled={!r.recognition.most_recognised.length} />}>
              <Recognition rec={r.recognition} />
            </Section>
          ) : null}

          <Section title="Taking part, by cluster" badge={clusters.length || null}>
            {!clusters.length ? <Empty>Nobody yet in this period.</Empty> : (
              <ul className="divide-y divide-gray-100">
                {clusters.map((c) => (
                  <li key={c.cluster} className={ROW}>
                    <span className="text-gray-900">{c.cluster}</span>
                    <span className="tabular-nums text-gray-600">{c.people} people · {c.points} pts</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
}

export default ReportsTab;
